// Copyright (c) 2019 by Michael Marucheck
//
// Use of this source code is governed by an MIT-style license that
// can be found in the LICENSE file distributed with this file.

import dateFnsFormat from "date-fns/format";
import dateFnsParse from "date-fns/parse";
import React, { useState, useRef } from "react";
import DayPickerInput from "react-day-picker/DayPickerInput";

import {
  isDate,
  isDuration,
  durationSeconds,
  isClassYearFormat,
  isClassYear,
  isPhone,
  UID_NEW,
  HrMin,
} from "../util";
import { Setter, textInputHandler, TextInputEvent } from "../UiUtil";

enum ValidationSeverity {
  ERROR,
  WARNING,
}

export interface Concern {
  severity: ValidationSeverity;
  message: string;
}

interface Validator<T> {
  isValid: (value: T) => boolean;
  severity: ValidationSeverity;
  message: string;
}

// Guarantees that error from earlier validator avoids evaluating later.
export function validate<T>(
  validators: Validator<T>[],
  value: T,
  debugOutput?: boolean
): Concern | null {
  debugOutput && debug(`validate '${value}'`);
  const result = validators.reduce((acc: Concern | null, validator) => {
    const { isValid, severity, message } = validator;
    if (acc != null) {
      if (
        acc.severity === ValidationSeverity.ERROR ||
        acc.severity === severity
      ) {
        debugOutput &&
          debug(`  keeping ${JSON.stringify(acc)}\n    rather than ${message}`);
        return acc;
      }
    }
    const valid = isValid(value);
    debugOutput && debug(`  got '${valid}' checking '${message}'`);
    return valid ? acc : { severity, message };
  }, null);
  debugOutput && debug(`  final result = '${JSON.stringify(result)}'`);
  return result;
}

function notValid<T>(_: T) {
  return false;
}

export function checkErr<T>(
  isValid: (value: T) => boolean,
  message: string
): Validator<T> {
  const severity = ValidationSeverity.ERROR;
  return { isValid: isValid || notValid, severity, message };
}

export function checkRegexErr(
  regex: RegExp,
  message: string
): Validator<string> {
  const severity = ValidationSeverity.ERROR;
  const isValid = (value: string) => regex.test(value);
  return { isValid: isValid, severity, message };
}

export function checkNotRegexErr(
  regex: RegExp,
  message: string
): Validator<string> {
  const severity = ValidationSeverity.ERROR;
  const isValid = (value: string) => !regex.test(value);
  return { isValid: isValid, severity, message };
}

export function checkWarn<T>(
  isValid: (value: T) => boolean,
  message: string
): Validator<T> {
  const severity = ValidationSeverity.WARNING;
  return { isValid: isValid || notValid, severity, message };
}

export function validatedDetailClass(
  isFocused: boolean,
  concern: Concern | null
): string {
  return !isFocused && isValidationError(concern) ? "detail error" : "detail";
}

export function concernMessageClass(concern: Concern): string {
  switch (concern.severity) {
    case ValidationSeverity.ERROR:
      return "error-message";
    case ValidationSeverity.WARNING:
      return "warning-message";
    default:
      throw new Error(`Unknown ValidationSeverity: ${concern.severity}`);
  }
}

export function isValidationError(concern: Concern | null) {
  return concern != null && concern.severity === ValidationSeverity.ERROR;
}

interface ValidatedDetailProps {
  htmlFor: string;
  label: string;
  concern: Concern | null;
}

export const ValidatedDetail: React.FC<ValidatedDetailProps> = ({
  htmlFor,
  label,
  concern,
  children,
}) => {
  const detailClass = validatedDetailClass(false, concern);
  debug(
    `ValidatedDetail class=${detailClass} concern=${JSON.stringify(concern)}`
  );

  return (
    <div className={detailClass}>
      <label htmlFor={htmlFor}>{label}</label>
      {children}
    </div>
  );
};

interface ValidatedTextInputProps {
  htmlId: string;
  label: string;
  inputMode?: string;
  type?: string;
  placeholder?: string;
  value: string;
  onChange: Setter<string>;
  concern: Concern | null;
}

export const ValidatedTextInputDetail: React.FC<ValidatedTextInputProps> = ({
  htmlId,
  label,
  inputMode,
  type,
  placeholder,
  value,
  onChange,
  concern,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const detailClass = validatedDetailClass(isFocused, concern);
  const concernClass = concern == null ? null : concernMessageClass(concern);

  if (concern != null) {
    debug(`TextInput: ${label}='${value}' concern=${JSON.stringify(concern)}`);
  }

  return (
    <>
      <div className={detailClass}>
        <label htmlFor={htmlId}>{label}</label>
        <input
          id={htmlId}
          type={type || "text"}
          placeholder={placeholder}
          value={value}
          inputMode={(inputMode as any) || "text"}
          onChange={textInputHandler(onChange)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
      </div>
      {concern != null && concernClass != null && (
        <div className="detail">
          <label />
          <div className={concernClass}>{concern.message}</div>
        </div>
      )}
    </>
  );
};

interface ValidatedTextAreaProps {
  htmlId: string;
  label: string;
  placeholder?: string;
  rows?: number;
  value: string;
  onChange: Setter<string>;
  concern: Concern | null;
}

export const ValidatedTextAreaDetail: React.FC<ValidatedTextAreaProps> = ({
  htmlId,
  label,
  placeholder,
  rows,
  value,
  onChange,
  concern,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const detailClass = validatedDetailClass(isFocused, concern);
  const concernClass = concern == null ? null : concernMessageClass(concern);

  if (concern != null) {
    debug(`TextInput: ${label}='${value}' concern=${JSON.stringify(concern)}`);
  }

  return (
    <>
      <div className={detailClass}>
        <label htmlFor={htmlId}>{label}</label>
        <textarea
          id={htmlId}
          placeholder={placeholder}
          value={value}
          onChange={textInputHandler(onChange)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          rows={rows || 2}
        />
      </div>
      {concern != null && concernClass != null && (
        <div className="detail">
          <label />
          <div className={concernClass}>{concern.message}</div>
        </div>
      )}
    </>
  );
};

interface ValidatedPhoneInputProps {
  htmlId: string;
  label: string;
  placeholder?: string;
  value: string;
  onChange: Setter<string>;
  concern: Concern | null;
}

export const ValidatedPhoneInputDetail: React.FC<ValidatedPhoneInputProps> = ({
  htmlId,
  label,
  placeholder,
  value,
  onChange,
  concern,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const detailClass = validatedDetailClass(isFocused, concern);
  const concernClass = concern == null ? null : concernMessageClass(concern);

  if (concern != null) {
    debug(`TextInput: ${label}='${value}' concern=${JSON.stringify(concern)}`);
  }

  function formatPhoneDigits(s: string) {
    if (!/^[0-9-]*[0-9]$/.test(s)) {
      debug(`PhoneInput: format ignoring '${s}'`);
      return s;
    }

    let formatted = "";
    for (let i = 0; i < s.length; i++) {
      const ch = s.charAt(i);
      if (/^[0-9]$/.test(ch)) {
        if (formatted.length === 3 || formatted.length === 7) {
          formatted += "-";
        }
        formatted += ch;
      }
    }
    debug(`PhoneInput: format from '${s}' to '${formatted}'`);
    return formatted;
  }

  function fixCursor(s: string, n: number): number {
    return (n > 0 && n < s.length && s.charAt(n - 1) === "-") ? n + 1 : n;
  }

  function manageCursorOnChange(e: TextInputEvent) {
    const { value: current, selectionStart: start, selectionEnd: end } = e.target;
    const update = formatPhoneDigits(e.target.value);
    // Need to update value so that cursor values are valid.
    e.target.value = update;
    if (/^[0-9-]*$/.test(current)) {
      if (start != null) {
        e.target.selectionStart = fixCursor(update, start);
      }
      if (end != null) {
        e.target.selectionEnd = fixCursor(update, end);
      }
    }
    return onChange(update);
  }

  return (
    <>
      <div className={detailClass}>
        <label htmlFor={htmlId}>{label}</label>
        <input
          id={htmlId}
          type={"text"}
          placeholder={placeholder}
          value={value}
          inputMode={"tel"}
          onChange={manageCursorOnChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
      </div>
      {concern != null && concernClass != null && (
        <div className="detail">
          <label />
          <div className={concernClass}>{concern.message}</div>
        </div>
      )}
    </>
  );
};

interface ValidatedDurationInputProps {
  htmlId: string;
  label: string;
  hoursPlaceholder?: string;
  hoursValue: string;
  onHoursChange: Setter<string>;
  minutesPlaceholder?: string;
  minutesValue: string;
  onMinutesChange: Setter<string>;
  concern: Concern | null;
}

export const ValidatedDurationInputDetail: React.FC<ValidatedDurationInputProps> = ({
  htmlId,
  label,
  hoursValue,
  onHoursChange,
  minutesValue,
  onMinutesChange,
  concern,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const detailClass = validatedDetailClass(isFocused, concern);
  const concernClass = concern == null ? null : concernMessageClass(concern);

  if (concern != null) {
    debug(`DurationInput: ${label}='${hoursValue}':'${minutesValue}' concern=${JSON.stringify(concern)}`);
  }

  return (
    <>
      <div className={detailClass}>
        <label htmlFor={`${htmlId}-hours`}>{label}</label>
        <table>
          <tbody>
            <tr>
              <td>
                <input
                  className="hours"
                  id={`${htmlId}-hours`}
                  type={"text"}
                  value={hoursValue}
                  inputMode={"decimal"}
                  onChange={textInputHandler(onHoursChange)}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                />&nbsp;h
            </td>
              <td>:</td>
              <td>
                <input
                  className="minutes"
                  id={`${htmlId}-minutes`}
                  type={"text"}
                  value={minutesValue}
                  inputMode={"decimal"}
                  onChange={textInputHandler(onMinutesChange)}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                />&nbsp;m
            </td>
            </tr>
          </tbody>
        </table>
      </div>
      {concern != null && concernClass != null && (
        <div className="detail">
          <label />
          <div className={concernClass}>{concern.message}</div>
        </div>
      )}
    </>
  );
};

interface ValidatedDayPickerProps {
  htmlId: string;
  label: string;
  placeholder?: string;
  value: string;
  onChange: Setter<string>;
  concern: Concern | null;
}

export const ValidatedDayPicker: React.FC<ValidatedDayPickerProps> = ({
  htmlId,
  label,
  value,
  onChange,
  concern,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const detailClass = validatedDetailClass(isFocused, concern);
  const concernClass = concern == null ? null : concernMessageClass(concern);

  const inputRef = useRef(null);

  function onShow() {
    setIsFocused(true);
    scrollDayPickerWorkaround(inputRef.current);
  }

  const DATE_FORMAT = "yyyy-MM-dd";

  function parseDate(text: string, format: string) {
    return dateFnsParse(text, format, new Date());
  }

  function formatDate(date: Date, format: string) {
    return dateFnsFormat(date, format);
  }

  if (concern != null) {
    debug(`DayPicker: ${label}='${value}' concern=${JSON.stringify(concern)}`);
  }

  return (
    <>
      <div className={detailClass}>
        <label htmlFor={htmlId}>{label}</label>
        <DayPickerInput
          format={DATE_FORMAT}
          formatDate={formatDate}
          parseDate={parseDate}
          value={parseDate(value, DATE_FORMAT)}
          onDayChange={x => onChange(formatDate(x, DATE_FORMAT))}
          hideOnDayClick={true}
          inputProps={{
            readOnly: true,
            ref: inputRef,
          }}
          onDayPickerShow={onShow}
          onDayPickerHide={() => setIsFocused(false)}
          dayPickerProps={{
            showOutsideDays: true,
            todayButton: "Go to Today",
          }}
        />
      </div>
      {concern != null && concernClass != null && (
        <div className="detail">
          <label />
          <div className={concernClass}>{concern.message}</div>
        </div>
      )}
    </>
  );
};

interface ValidatedCreateButtonProps {
  disabled: boolean;
  buttonText: string;
  onClick: any;
  concern: Concern | null;
}
export const ValidatedCreateButton: React.FC<ValidatedCreateButtonProps> = ({
  disabled,
  buttonText,
  onClick,
  concern,
}) => {
  const detailClass = validatedDetailClass(false, concern);
  const concernClass = concern == null ? null : concernMessageClass(concern);

  return (
    <>
      {concern != null && concernClass != null && (
        <div className={detailClass}>
          <label />
          <div className={concernClass}>
            <button type="button" onClick={onClick} disabled={disabled}>
              {buttonText}
            </button>
            {concern.message}
          </div>
        </div>
      )}
    </>
  );
};

function scrollDayPickerWorkaround(elem: HTMLElement | null) {
  if (elem != null) {
    setTimeout(() => {
      elem.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "center",
      });
    }, 300);
  }
}

export const UID_VALIDATORS = [
  checkErr<string>(x => x !== "", ""),
  checkErr<string>(x => x !== UID_NEW, `Item not yet created`),
];

export const FIRST_LAST_NAME_VALIDATORS = [
  // checkRegexErr(/^[\p{Letter} .-]*$/u, "Unexpected character in name"),
  checkNotRegexErr(/^ /, "Value should not start with a space"),
  checkNotRegexErr(/ $/, "Value should not end with a space"),
  checkNotRegexErr(/ {2,}/, "Value should have just one space between names"),
  checkRegexErr(
    /^[^ ]+( [^ ]+)+$/u,
    "Value should include first and last name with a space between them"
  ),
  // checkRegexErr(
  //   /^[\p{Letter}.-]+( [\p{Letter}.-]+)+$/u,
  //   "Value should include first and last name with a space between them"
  // ),
];

export const ONE_OR_MORE_NAME_VALIDATORS = [
  // checkRegexErr(/^[\p{Letter} .-]*$/u, "Unexpected character in name"),
  checkNotRegexErr(/^$/, "Value cannot be empty"),
  checkNotRegexErr(/^ /, "Value should not start with a space"),
  checkNotRegexErr(/ $/, "Value should not end with a space"),
  checkNotRegexErr(/ {2,}/, "Value should have just one space between names"),
];

export const OPTIONAL_NAME_VALIDATORS = [
  // checkRegexErr(/^[\p{Letter} .-]*$/u, "Unexpected character in name"),
  checkNotRegexErr(/^ /, "Value should not start with a space"),
  checkNotRegexErr(/ $/, "Value should not end with a space"),
  checkNotRegexErr(/ {2,}/, "Value should have just one space between names"),
];

export const EMAIL_VALIDATORS = [
  checkRegexErr(/^[a-zA-Z0-9@._-]*$/u, "Unexpected character in name"),
  checkRegexErr(
    /^[a-zA-Z0-9._-]+@[a-z0-9-]+(.[a-z0-9])+/u,
    "Expected email like user@host.domain"
  ),
];

export const PHONE_NUMBER_VALIDATORS = [
  // checkRegexErr(/^[-.() 0-9]*$/, "Unexpected character in phone number"),
  checkRegexErr(/^[0-9-]*$/, "Unexpected character in phone number"),
  checkNotRegexErr(/^[01]/, "Phone number should not start with 0 or 1"),
  checkNotRegexErr(/^-/, "Phone number should not start with a hyphen"),
  checkErr<string>(isPhone, "Value should have 10 digits"),
];

export const DATE_VALIDATORS = [
  checkErr<string>(isDate, "Value should look like YYYY-MM-DD"),
];

export const CLASS_YEAR_VALIDATORS = [
  checkErr<string>(isClassYearFormat, "Value should look like YYYY"),
  checkErr<string>(isClassYear, "Value should be close to current year"),
];

export const DURATION_VALIDATORS = [
  checkErr<string>(
    isDuration,
    "Value should look like '1' for hours or '1:00' for hours and minutes."
  ),
  checkErr<string>(
    duration => durationSeconds(duration) > 0,
    "Value should be greater than 0."
  ),
  checkWarn<string>(
    duration => durationSeconds(duration) < 40 * 60 * 60,
    "That is a lot of time.  Is this intentional?"
  ),
];

export const HRMIN_VALIDATORS = [
  checkErr<HrMin>(
    hrmin => /^[0-9]*$/.test(hrmin.hours),
    "Hours should be a number"
  ),
  checkErr<HrMin>(
    hrmin => /^[0-9]*$/.test(hrmin.minutes),
    "Minutes should be a number"
  ),
  checkErr<HrMin>(
    hrmin => hrmin.minutes === "" || Number.parseInt(hrmin.minutes) < 60,
    "Minutes should be less than 60"
  ),
  checkErr<HrMin>(
    hrmin => Number.parseInt(hrmin.hours) > 0,
    "Hours should be greater than 0"
  ),
  checkWarn<HrMin>(
    hrmin => Number.parseInt(hrmin.hours) < 40,
    "That is a lot of hours.  Is this intentional?"
  ),
];

function debug(message: string) {
  if (false) {
    console.log(`ValidatedUI: ${message}`);
  }
}
